"use client"
import * as DropdownPrimitive from "@radix-ui/react-dropdown-menu"
import useMergedRef from "@react-hook/merged-ref"
import React, {
  ComponentProps,
  ComponentPropsWithoutRef,
  ElementRef,
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react"
import { VariantProps, tv } from "tailwind-variants"
import { useDebouncedCallback } from "use-debounce"
import { disabledVariants } from "../../styles"
import { tooltipVariants } from "../../styles/utils/tooltip"
import { classNames } from "../../utils"
import { HydrationGuard } from "../HydrationGuard"
import { Item, ItemProps } from "../Item"
import { Separator, SeparatorProps } from "../Separator"

export type DropdownSize = Pick<
  VariantProps<typeof dropdownItemVariants>,
  "size"
>["size"]

const DropdownContext = createContext<{
  size: NonNullable<DropdownSize>
}>({
  size: "sm",
})

export type DropdownProps = Pick<
  ComponentPropsWithoutRef<typeof DropdownPrimitive.Root>,
  "open" | "defaultOpen" | "onOpenChange"
> &
  VariantProps<typeof tooltipVariants> &
  Omit<
    ComponentPropsWithoutRef<typeof DropdownPrimitive.Content>,
    "content"
  > & {
    content: React.ReactNode
    trigger?: "click" | "hover"
    overrides?: {
      Content?: {
        className?: string
      }
    }
  } & Pick<VariantProps<typeof dropdownItemVariants>, "size">

const DropdownBase = forwardRef<
  ElementRef<typeof DropdownPrimitive.Trigger>,
  Omit<DropdownProps, "onValueChange" | "selection">
>(function Dropdown(
  {
    content,
    open,
    defaultOpen,
    onOpenChange,
    children,
    className,
    overrides,
    forceMount,
    trigger = "click",
    size = "sm",
    ...props
  },
  ref,
) {
  const contextValue = useMemo(
    () => ({
      size,
    }),
    [size],
  )
  const triggerRef = useRef<HTMLButtonElement>(null)
  const mergedRef = useMergedRef(ref, triggerRef)
  // We need to keep track of the open state internally so we can account for hover variant
  const [openedState, setOpenedState] = useState(defaultOpen ?? open)
  const resolvedOpenState = open === undefined ? openedState : open
  const handleOnOpenChange = useCallback(
    (open: boolean) => {
      setOpenedState(open)
      onOpenChange?.(open)
    },
    [onOpenChange],
  )

  const debouncedHandleOnOpenChange = useDebouncedCallback(
    handleOnOpenChange,
    100,
  )

  const hoverTriggerOnPointerEnter =
    trigger === "hover"
      ? () => {
          debouncedHandleOnOpenChange(true)
        }
      : undefined

  const hoverTriggeronPointerLeave =
    trigger === "hover"
      ? () => {
          debouncedHandleOnOpenChange(false)
        }
      : undefined

  return (
    <DropdownContext.Provider value={contextValue}>
      <DropdownPrimitive.Root
        defaultOpen={defaultOpen}
        // This is needed so interaction with outside elements can still happen on hover
        modal={trigger !== "hover"}
        open={resolvedOpenState}
        onOpenChange={handleOnOpenChange}
      >
        <DropdownPrimitive.Trigger
          asChild
          className={classNames("focus-visible:outline-hidden", className)}
          ref={mergedRef}
          onPointerEnter={hoverTriggerOnPointerEnter}
          onPointerLeave={hoverTriggeronPointerLeave}
        >
          {children}
        </DropdownPrimitive.Trigger>

        {/* https://github.com/radix-ui/primitives/issues/1386 */}
        <HydrationGuard>
          <DropdownPrimitive.Portal forceMount={forceMount}>
            {content && (
              <DropdownPrimitive.Content
                className={classNames(
                  tooltipVariants({
                    animation: trigger === "hover" ? "slide-out" : "fade-in",
                  }),
                  "p-1 rounded-lg",
                  "overflow-auto font-normal",
                  "w-[var(--radix-dropdown-menu-trigger-width)]",
                  "max-h-[450px] min-w-max",
                  overrides?.Content?.className,
                )}
                // Don't auto focus the trigger when we are on hover mode.
                sideOffset={4}
                onCloseAutoFocus={
                  trigger === "hover" ? e => e.preventDefault() : undefined
                }
                onPointerEnter={hoverTriggerOnPointerEnter}
                onPointerLeave={hoverTriggeronPointerLeave}
                {...props}
              >
                {content}
              </DropdownPrimitive.Content>
            )}
          </DropdownPrimitive.Portal>
        </HydrationGuard>
      </DropdownPrimitive.Root>
    </DropdownContext.Provider>
  )
})

const dropdownItemVariants = tv({
  base: classNames(
    "rounded border-0 focus:bg-bg-additional-1 focus:outline-hidden",
    disabledVariants(),
  ),
  variants: {
    size: {
      sm: "gap-2.5 px-2 py-1.5",
      md: "px-3 py-4",
    },
  },
})

export type DropdownItemProps = ComponentProps<typeof DropdownPrimitive.Item> &
  Omit<ItemProps, "onSelect"> &
  Omit<VariantProps<typeof dropdownItemVariants>, "size"> & {
    closeOnSelect?: boolean
  }

const DropdownItemBase = forwardRef<
  ElementRef<typeof DropdownPrimitive.Item> & HTMLButtonElement,
  DropdownItemProps
>(function DropdownItem(
  {
    asChild,
    children,
    className,
    disabled,
    closeOnSelect = true,
    onSelect,
    ...rest
  },
  ref,
) {
  const { size } = useContext(DropdownContext)

  return (
    <DropdownPrimitive.Item
      {...rest}
      asChild
      disabled={disabled}
      ref={ref}
      // This prevents the dropdown item being selected from onPointerUp, which is a weird UX
      // https://github.com/radix-ui/primitives/blob/c31c97274ff357aea99afe6c01c1c8c58b6356e0/packages/react/menu/src/Menu.tsx#L645
      onPointerUp={e => e.preventDefault()}
      onSelect={event => {
        if (!closeOnSelect) {
          event.preventDefault()
        }

        onSelect?.(event)
      }}
    >
      <Item
        asChild={asChild}
        className={classNames(dropdownItemVariants({ size }), className)}
        disabled={disabled}
        interactive
        ref={ref}
      >
        {children}
      </Item>
    </DropdownPrimitive.Item>
  )
})

const dropdownAvatarVariants = tv({
  variants: {
    size: {
      sm: "size-4",
      md: "",
    },
  },
})

export const DropdownItemAvatar = ({
  className,
  ...rest
}: ComponentProps<typeof Item.Avatar>) => {
  const { size } = useContext(DropdownContext)

  return (
    <Item.Avatar
      className={classNames(dropdownAvatarVariants({ size }), className)}
      {...rest}
    />
  )
}

export const DropdownItemTitle = (props: ComponentProps<typeof Item.Title>) => {
  const { size } = useContext(DropdownContext)

  return (
    <Item.Title weight={size === "sm" ? "regular" : "semibold"} {...props} />
  )
}

export const DropdownItemDescription = Item.Description

export const DropdownItemContent = Item.Content

export const DropdownItem = Object.assign(DropdownItemBase, {
  Avatar: DropdownItemAvatar,
  Title: DropdownItemTitle,
  Description: DropdownItemDescription,
  Content: DropdownItemContent,
})

export const DropdownSeparator = forwardRef<
  ElementRef<typeof Separator>,
  SeparatorProps
>(function DropdownSeparator({ className, ...rest }, ref) {
  return (
    <Separator
      className={classNames("my-0.5", className)}
      ref={ref}
      {...rest}
    />
  )
})

/**
 * Displays a menu to the user — such as a set of actions or functions — triggered by a button.
 */
export const Dropdown = Object.assign(DropdownBase, {
  Item: DropdownItem,
  Separator: DropdownSeparator,
})
